import { useEffect, useState } from "react";
import {
    Modal,
    ModalDialog,
    Typography,
    Button,
    Stack,
    IconButton,
    CircularProgress,
    Box,
    Divider,
} from "@mui/joy";
import { ContentCopy, Close, Check } from "@mui/icons-material";
import { WorkflowApi } from "../api/workflow";
import Markdown from "react-markdown";

const styles = {
    modalDialog: {
        width: 800,
        maxWidth: "90%",
        position: "relative",
    },
    actionButton: { minWidth: 140 },
    closeButton: {
        position: "absolute",
        top: 8,
        right: 8,
    },
    loadingBox: {
        display: "flex",
        justifyContent: "center",
        my: 4,
    },
    title: {
        fontFamily: "Overpass",
    },
    errorText: {
        marginBottom: 3,
    },
    content: {
        marginBottom: 3,
    },
};

interface RfiModalProps {
    open: boolean;
    onClose: () => void;
    node1Id: string;
    node2Id: string;
    description: string;
}

export default function RfiModal({ open, onClose, node1Id, node2Id, description }: RfiModalProps) {
    const [rfiText, setRfiText] = useState<string>("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(rfiText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    };

    useEffect(() => {
        if (open) {
            setIsLoading(true);
            setError(null);
            WorkflowApi.generateDraftRfi(node1Id, node2Id, description)
                .then((text) => {
                    setRfiText(text);
                })
                .catch((err) => {
                    setError("Failed to generate RFI draft. Please try again.");
                    console.error("RFI generation error:", err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [open, node1Id, node2Id, description]);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={styles.modalDialog}>
                <Typography level="title-md" sx={styles.title}>
                    Draft RFI
                </Typography>

                <IconButton onClick={onClose} sx={styles.closeButton}>
                    <Close />
                </IconButton>

                <Divider />

                {isLoading ? (
                    <Box sx={styles.loadingBox}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Typography color="danger" sx={styles.errorText}>
                        {error}
                    </Typography>
                ) : (
                    <Typography sx={styles.content}>
                        <Markdown>{rfiText}</Markdown>
                    </Typography>
                )}

                <Divider />

                <Stack direction="row" justifyContent="center" spacing={2}>
                    <Button
                        variant="plain"
                        color={copied ? "success" : "neutral"}
                        startDecorator={copied ? <Check /> : <ContentCopy />}
                        onClick={handleCopyToClipboard}
                        sx={styles.actionButton}
                        size="sm"
                    >
                        {copied ? "Copied!" : "Copy"}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}
